import React from 'react'
import { ThemeProvider } from 'styled-components'
import { Provider } from 'react-redux'
import { theme } from '../theme'
import { initializeStore, StoreType } from '../store'
import GlobalStyles from '../components/GlobalStyles'
import { dictionaryKeys, LocaleProvider, StringProvider } from '../components/Locale'
import Index, { Subject, Location as SchoolLocation } from '../pages/index'
import moment from 'moment'
import { Spinner } from '@webnuts/university-greenland-shared-components'
import { Placement } from '@webnuts/shared-components'

type AppProps = {}

export type CommonAppState = {
  locations: SchoolLocation[]
  subjects: Subject[]
  availableWeekdays: (location?: SchoolLocation) => number[]
  isDateDisabled: (date: Date, location?: SchoolLocation) => boolean
}

type AppState = CommonAppState & {
  loading: boolean
  translate: StringProvider
}

// todo: this should probably be an env. variable
export const isDevelopment =
  location.hostname === 'localhost' ||
  location.hostname === '127.0.0.1' ||
  location.hostname === 'unigl-student-counselling.herokuapp.com'

export const domain = isDevelopment ? 'https://da.uni.gl' : ''

export default class App extends React.Component<AppProps, AppState> {
  store: StoreType

  constructor(props: AppProps) {
    super(props)

    this.store = initializeStore()

    this.state = {
      loading: true,
      translate: (x) => x,
      locations: [],
      subjects: [],
      availableWeekdays: (location) => {
        if (location == null) {
          return [0, 1, 2, 3, 4, 5, 6]
        }

        const result: number[] = []

        if (location.availability.disableSundays) {
          result.push(0)
        }

        if (location.availability.disableMondays) {
          result.push(1)
        }

        if (location.availability.disableTuesdays) {
          result.push(2)
        }

        if (location.availability.disableWednesdays) {
          result.push(3)
        }

        if (location.availability.disableThursdays) {
          result.push(4)
        }

        if (location.availability.disableFridays) {
          result.push(5)
        }

        if (location.availability.disableSundays) {
          result.push(6)
        }

        return result
      },
      isDateDisabled: (date, location) => {
        return location == null
          ? true
          : location.availability.disabledDates.some((disabledDate) => moment(disabledDate).isSame(moment(date), 'day'))
      }
    }

    const fetchDictionaryValues = fetch(
      `${domain}/umbraco/surface/Dictionary/Find?${dictionaryKeys.map((k) => `keys=${k}`).join('&')}`
    ).then((response) => response.json())

    const fetchInitialAppState = fetch(`${domain}/umbraco/surface/StudentCounselling/Find`).then((response) =>
      response.json()
    )

    Promise.all([fetchDictionaryValues, fetchInitialAppState]).then(([dictionaryValues, initialAppState]) => {
      initialAppState.locations = initialAppState.locations.map((l: any) => {
        l.availability.disabledDates = l.availability.disabledDates.map((d: any) => moment(d, 'DD-MM-YYYY').toDate())
        return l
      })

      this.setState({
        loading: false,
        locations: initialAppState.locations as SchoolLocation[],
        subjects: initialAppState.subjects as Subject[],
        translate: (key) => {
          const value = dictionaryValues.result[key]
          if (value != null) {
            return value
          } else {
            // tslint:disable-next-line:no-console
            console.warn(`Missing translation for key '${key}'`)
            return key
          }
        }
      })
    })
  }

  render() {
    return (
      <ThemeProvider theme={theme}>
        <LocaleProvider locale="da" translate={this.state.translate}>
          <Provider store={this.store}>
            {this.state.loading ? (
              <Placement horizontal={'center'}>
                <Spinner />
              </Placement>
            ) : (
              <Index
                isDateDisabled={this.state.isDateDisabled}
                availableWeekdays={this.state.availableWeekdays}
                locations={this.state.locations}
                subjects={this.state.subjects}
              />
            )}
          </Provider>
          <GlobalStyles />
        </LocaleProvider>
      </ThemeProvider>
    )
  }
}
